import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import LandingPage from "./pages/LandingPage";
import StatusPage from "./pages/StatusPage";
import IncidentsPage from "./pages/IncidentsPage";
import KycReview from "./pages/KycReview";
import AdminLayout from "./components/AdminLayout";
import MerchantLayout from "./components/MerchantLayout";
import Dashboard from "./pages/Dashboard";
import Merchants from "./pages/Merchants";
import MerchantDetail from "./pages/MerchantDetail";
import Policies from "./pages/Policies";
import Claims from "./pages/Claims";
import FraudQueue from "./pages/FraudQueue";
import PremiumLedger from "./pages/PremiumLedger";
import AuditLogs from "./pages/AuditLogs";
import Compliance from "./pages/Compliance";
import KFS from "./pages/KFS";
import Loyalty from "./pages/Loyalty";
import Payouts from "./pages/Payouts";
import DigestPreview from "./pages/DigestPreview";
import ReferralLeaderboard from "./pages/ReferralLeaderboard";
import MerchantDashboard from "./pages/merchant/MerchantDashboard";
import FileClaim from "./pages/merchant/FileClaim";
import PartnerAdmin from "./pages/PartnerAdmin";
import Security from "./pages/Security";
import EmbedSignup from "./pages/EmbedSignup";
import EmbedDocs from "./pages/EmbedDocs";
import AdminUsers from "./pages/AdminUsers";
import MerchantTeam from "./pages/merchant/Team";
import Settings from "./pages/Settings";
import Webhooks from "./pages/Webhooks";
import PartnerAnalyticsEmbed from "./pages/PartnerAnalyticsEmbed";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Support from "./pages/Support";

function AuthLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f4f2eb]" data-testid="auth-loading">
      <Loader2 className="w-6 h-6 animate-spin text-[#114232]" />
    </div>
  );
}

function ProtectedRoute({ children, allow = "admin" }) {
  const { user } = useAuth();
  if (user === null) return <AuthLoading />;
  if (!user) return <Navigate to="/login" replace />;
  const role = user.role;
  const isMerchantUser = role === "merchant" || role === "merchant_staff";
  const isPartnerUser =
    role === "partner_admin" || role === "partner_analyst" || role === "partner_viewer";
  if (allow === "admin" && isMerchantUser) return <Navigate to="/merchant" replace />;
  if (allow === "admin" && isPartnerUser) return <Navigate to="/partner" replace />;
  if (allow === "merchant" && !isMerchantUser) return <Navigate to="/app" replace />;
  if (allow === "partner_admin" && !isPartnerUser && role !== "super_admin")
    return <Navigate to={isMerchantUser ? "/merchant" : "/app"} replace />;
  return children;
}

// `/` always shows the public landing page. Signed-in users see a
// "Continue to dashboard" CTA inside the landing nav instead of being
// silently redirected, so they can still preview marketing copy.
function RootRoute() {
  const { user } = useAuth();
  if (user === null) return <AuthLoading />;
  return <LandingPage />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/support" element={<Support />} />
            <Route element={<ProtectedRoute allow="admin"><AdminLayout /></ProtectedRoute>}>
              <Route path="/app" element={<Dashboard />} />
              <Route path="/merchants" element={<Merchants />} />
              <Route path="/merchants/:id" element={<MerchantDetail />} />
              <Route path="/policies" element={<Policies />} />
              <Route path="/claims" element={<Claims />} />
              <Route path="/fraud" element={<FraudQueue />} />
              <Route path="/loyalty" element={<Loyalty />} />
              <Route path="/ledger" element={<PremiumLedger />} />
              <Route path="/audit" element={<AuditLogs />} />
              <Route path="/compliance" element={<Compliance />} />
              <Route path="/incidents" element={<IncidentsPage />} />
              <Route path="/kyc" element={<KycReview />} />
              <Route path="/payouts" element={<Payouts />} />
              <Route path="/digest" element={<DigestPreview />} />
              <Route path="/referrals" element={<ReferralLeaderboard />} />
              <Route path="/kfs" element={<KFS />} />
              <Route path="/security" element={<Security />} />
              <Route path="/users" element={<AdminUsers />} />
              <Route path="/webhooks" element={<Webhooks />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
            <Route element={<ProtectedRoute allow="merchant"><MerchantLayout /></ProtectedRoute>}>
              <Route path="/merchant" element={<MerchantDashboard />} />
              <Route path="/merchant/claim" element={<FileClaim />} />
              <Route path="/merchant/kfs" element={<KFS />} />
              <Route path="/merchant/team" element={<MerchantTeam />} />
              <Route path="/merchant/settings" element={<Settings />} />
            </Route>
            <Route path="/partner" element={
              <ProtectedRoute allow="partner_admin"><PartnerAdmin /></ProtectedRoute>
            } />
            <Route path="/embed/signup" element={<EmbedSignup />} />
            <Route path="/embed/partner-analytics" element={<PartnerAnalyticsEmbed />} />
            <Route path="/docs/embed" element={<EmbedDocs />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
